import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CorporateAccountOpenProfileDetailsPages, useCorporateAccountOpenProfileDetailsPageTypeStore } from 'views/pages/accountOpenProfileDetails/corporateProfileDetails/hooks';
import { useEffect } from 'react';
import { useCorporateAccountEstablishStore } from '../hooks';
import { ErrorWord, getMaxLengthErrorMessage, TemplateProps, yupNumberString } from '../common';
import { AddressInfo } from 'views/templates/module/addressInput/hook';
import { formErrorScroll } from 'views/mutual/formErrorScroll';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';

export type RepresentInfoType = {
  representFirstName: string;
  representLastName: string;
  representFirstNameKana: string;
  representLastNameKana: string;
  representBirthDay: Date | null;
  representPostCode: string;
  representPrefecture: string;
  representCityCountry: string;
  representTownStreetAddress: string;
  hasNoRepresentTownStreetAddress: boolean; // リクエストに不要
  representBuildingNameAndRoomNumber: string;
  representMobilePhoneNumber: string;
  representPosition: string;
  representMailAddress: string;
  representConfirmMailAddress: string; // リクエストに不要
};

export const useRepresentInfo = ({ isUpdate }: TemplateProps) => {
  const { param, add } = useCorporateAccountEstablishStore();
  const { setAccountOpenProfileDetailsPageType } = useCorporateAccountOpenProfileDetailsPageTypeStore();
  const { messageClose } = useGlobalToastHandler();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validation = Yup.object().shape({
    representFirstName: Yup.string().required(ErrorWord.INPUT).max(40, getMaxLengthErrorMessage(40)),
    representLastName: Yup.string().required(ErrorWord.INPUT).max(80, getMaxLengthErrorMessage(80)),
    representFirstNameKana: Yup.string().required(ErrorWord.INPUT).matches(/^[ァ-ヴー_]+$/, ErrorWord.FURIGANA_KATAKANA).max(20, getMaxLengthErrorMessage(20)),
    representLastNameKana: Yup.string().required(ErrorWord.INPUT).matches(/^[ァ-ヴー_]+$/, ErrorWord.FURIGANA_KATAKANA).max(20, getMaxLengthErrorMessage(20)),
    representBirthDay: Yup.date().required(ErrorWord.Date),
    representPostCode: yupNumberString({ length: 7, isRequired: true, errorWord: ErrorWord.POSTCODE }),
    representPrefecture: Yup.string().required(ErrorWord.SELECT),
    representCityCountry: Yup.string().required(ErrorWord.INPUT).max(40, getMaxLengthErrorMessage(40)),
    representTownStreetAddress: Yup.string().when('hasNoRepresentTownStreetAddress', {
      is: false,
      then: (schema) => schema.required(ErrorWord.INPUT),
      otherwise: (schema) => schema.strip(),
    }).max(40, getMaxLengthErrorMessage(40)),
    representBuildingNameAndRoomNumber : Yup.string().max(40, getMaxLengthErrorMessage(40)),
    representMobilePhoneNumber: yupNumberString({ length: 11, isRequired: true, errorWord: ErrorWord.MOBILE_PHONE }),
    representPosition : Yup.string().max(128, getMaxLengthErrorMessage(128)),
    representMailAddress: Yup.string().trim().email(ErrorWord.EMAIL).max(255, getMaxLengthErrorMessage(255)),
    representConfirmMailAddress: Yup.string().when(['representMailAddress'], {
      is: (mail: string) => mail,
      then: (schema) =>
        schema.required(ErrorWord.EMAIL_CONFIRM).oneOf([Yup.ref('representMailAddress')], ErrorWord.EMAIL_CONFIRM),
      otherwise: (schema) => schema.strip(),
    }),
  });

  const initialValues: RepresentInfoType = {
    representFirstName: param.representFirstName ?? '',
    representLastName: param.representLastName ?? '',
    representFirstNameKana: param.representFirstNameKana ?? '',
    representLastNameKana: param.representLastNameKana ?? '',
    representBirthDay: param.representBirthDay ?? null,
    representPostCode: param.representPostCode ?? '',
    representPrefecture: param.representPrefecture ?? '',
    representCityCountry: param.representCityCountry ?? '',
    representTownStreetAddress: param.representTownStreetAddress ?? '',
    hasNoRepresentTownStreetAddress: param.hasNoRepresentTownStreetAddress ?? false,
    representBuildingNameAndRoomNumber: param.representBuildingNameAndRoomNumber ?? '',
    representMobilePhoneNumber: param.representMobilePhoneNumber ?? '',
    representPosition: param.representPosition ?? '',
    representMailAddress: param.representMailAddress ?? '',
    representConfirmMailAddress: param.representConfirmMailAddress ?? '',
  };

  const formik = useFormik<RepresentInfoType>({
    enableReinitialize: isUpdate,
    initialValues,
    async onSubmit() {
      add({
        ...param,
        ...formik.values,
      });
      setAccountOpenProfileDetailsPageType(CorporateAccountOpenProfileDetailsPages.dealerInfo);
    },
    validationSchema: validation,
    validateOnChange: false,
    validateOnBlur: false,
    validate: () => {
      // バリデーションかける時に必ずエラートーストを閉じる
      messageClose();
    },
  });

  const representFirstNameProps = {
    value: formik.values.representFirstName,
    onChange: (value: string) => formik.setFieldValue('representFirstName', value),
    error: formik.errors.representFirstName,
  };

  const representLastNameProps = {
    value: formik.values.representLastName,
    onChange: (value: string) => formik.setFieldValue('representLastName', value),
    error: formik.errors.representLastName,
  };

  const representFirstNameKanaProps = {
    value: formik.values.representFirstNameKana,
    onChange: (value: string) => formik.setFieldValue('representFirstNameKana', value),
    error: formik.errors.representFirstNameKana,
  };

  const representLastNameKanaProps = {
    value: formik.values.representLastNameKana,
    onChange: (value: string) => formik.setFieldValue('representLastNameKana', value),
    error: formik.errors.representLastNameKana,
  };

  const representBirthDayProps = {
    date: formik.values.representBirthDay,
    name: 'representBirthDay',
    minDate: new Date('1900/1/1'),
    maxDate: new Date(),
    onChange: (value: any) => formik.setFieldValue('representBirthDay', value),
    onBlur: formik.handleBlur,
    isWeekEndDisable: false,
    error: formik.errors.representBirthDay,
  };

  const representAddressPorps = {
    postCode: {
      value: formik.values.representPostCode,
      onChange: (value: string) => {
        formik.setFieldValue('representPostCode', value);
      },
      error: formik.errors.representPostCode,
    },
    prefecture: {
      selectedOption: formik.values.representPrefecture,
      setSelectedOption: (value: string) => formik.setFieldValue('representPrefecture', value),
      error: formik.errors.representPrefecture,
    },
    municipality: {
      value: formik.values.representCityCountry,
      onChange: (value: string) => {
        formik.setFieldValue('representCityCountry', value);
      },
      error: formik.errors.representCityCountry,
    },
    streetAddress: {
      value: formik.values.representTownStreetAddress,
      onChange: (value: string) => {
        formik.setFieldValue('representTownStreetAddress', value);
      },
      error: formik.errors.representTownStreetAddress,
      disabled: formik.values.hasNoRepresentTownStreetAddress,
    },
    hasNoTownStreetAddress: {
      checked: formik.values.hasNoRepresentTownStreetAddress,
      setChecked: (value: boolean) => {
        formik.setFieldValue('representTownStreetAddress', '');
        formik.setFieldValue('hasNoRepresentTownStreetAddress', value);
      },
    },
    buildingNameAndRoomNumber: {
      value: formik.values.representBuildingNameAndRoomNumber,
      onChange: (value: string) => formik.setFieldValue('representBuildingNameAndRoomNumber', value),
      error: formik.errors.representBuildingNameAndRoomNumber,
    },
    onSearchAddress: (addressInfo: AddressInfo) => {
      formik.setFieldValue('representPrefecture', addressInfo.prefecture);
      formik.setFieldValue('representCityCountry', addressInfo.cityCountry);
      formik.setFieldValue('hasNoRepresentTownStreetAddress', false);
      formik.setFieldValue('representTownStreetAddress', addressInfo.townStreetAddress);
      formik.setFieldValue('representBuildingNameAndRoomNumber', '');
    },
  };

  const representMobilePhoneNumberProps = {
    value: formik.values.representMobilePhoneNumber,
    onChange: (value: string) => formik.setFieldValue('representMobilePhoneNumber', value),
    error: formik.errors.representMobilePhoneNumber,
  };

  const representPositionProps = {
    value: formik.values.representPosition,
    onChange: (value: string) => formik.setFieldValue('representPosition', value),
    error: formik.errors.representPosition,
  };

  const representMailAddressPorps = {
    value: formik.values.representMailAddress,
    onChange: (value: string) => formik.setFieldValue('representMailAddress', value),
    error: formik.errors.representMailAddress,
  };

  const representConfirmMailAddressPorps = {
    value: formik.values.representConfirmMailAddress,
    onChange: (value: string) => formik.setFieldValue('representConfirmMailAddress', value),
    error: formik.errors.representConfirmMailAddress,
  };

  useEffect(() => {
    formErrorScroll();
  }, [formik.isSubmitting]);

  return {
    formik,
    representFirstNameProps,
    representLastNameProps,
    representFirstNameKanaProps,
    representLastNameKanaProps,
    representBirthDayProps,
    representAddressPorps,
    representMobilePhoneNumberProps,
    representPositionProps,
    representMailAddressPorps,
    representConfirmMailAddressPorps,
  };
};
